import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { CommissionNewBusinessParams, CommissionNewBusinessResponse } from '@/lib/types';

/**
 * One person's per-account new-business breakdown for one period
 * (`GET /commissions/progress/new-business`, owner 2026-10-28).
 *
 * - `params === null` (modal closed, nothing chosen) is `idle`: nothing is fetched.
 * - A 403 is `forbidden`, not an error: only the person themselves and
 *   `commissions.read` scope `all` may read it.
 * - A 404 is `not-found`: unknown period, or no assignment and no remainder rule.
 * - `loading` is derived from a stamped key, so a change of person or period
 *   reads as loading rather than showing the previous answer for a frame.
 */
export type CommissionNewBusinessStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'forbidden'
  | 'not-found'
  | 'error';

export interface CommissionNewBusinessState {
  status: CommissionNewBusinessStatus;
  data: CommissionNewBusinessResponse | null;
  error: Error | null;
  reload: () => void;
}

interface Answer {
  key: string;
  status: 'ready' | 'forbidden' | 'not-found' | 'error';
  data: CommissionNewBusinessResponse | null;
  error: Error | null;
}

function isForbidden(caught: unknown): boolean {
  return caught instanceof ApiError && (caught.status === 403 || caught.code === 'FORBIDDEN');
}

function isNotFound(caught: unknown): boolean {
  return caught instanceof ApiError && (caught.status === 404 || caught.code === 'NOT_FOUND');
}

export function useCommissionNewBusiness(
  params: CommissionNewBusinessParams | null,
): CommissionNewBusinessState {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const userId = params === null ? null : params.userId;
  const periodId = params === null ? null : params.periodId;
  const key = userId === null || periodId === null ? '' : `${userId}|${periodId}|${reloadToken}`;

  useEffect(() => {
    if (userId === null || periodId === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stamp = `${userId}|${periodId}|${reloadToken}`;

    api
      .commissionNewBusiness({ userId, periodId }, signal)
      .then((data) => {
        if (signal.aborted) return;
        setAnswer({ key: stamp, status: 'ready', data, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        if (isForbidden(caught)) {
          setAnswer({ key: stamp, status: 'forbidden', data: null, error: null });
          return;
        }
        if (isNotFound(caught)) {
          setAnswer({ key: stamp, status: 'not-found', data: null, error: null });
          return;
        }
        setAnswer({
          key: stamp,
          status: 'error',
          data: null,
          error: caught instanceof Error ? caught : new Error('Could not read new business.'),
        });
      });

    return () => controller.abort();
  }, [userId, periodId, reloadToken]);

  if (key === '') return { status: 'idle', data: null, error: null, reload };
  const current = answer !== null && answer.key === key ? answer : null;
  if (current === null) return { status: 'loading', data: null, error: null, reload };
  return { status: current.status, data: current.data, error: current.error, reload };
}
